/**
 * Shared helpers for the inline per-section editing pattern (master-doc Part A)
 * rolled across every profile overview: the per-FIELD attribution map, the
 * "latest {by,at} in a section" reducer, and the org-settings-registry → select
 * options builder. Extracted from ClientDetail so Property/Parcel/Tax-bill/
 * Tax-return/Assessor/Collector/Jurisdiction all derive their section attribution
 * and dropdown options identically.
 */
import { sortOptionsByLabel } from '../atoms/optionOrder';

/** Per-field attribution: field_key → who last changed it + when. */
export type AttrMap = Record<string, { by: string | null; at: string | null }>;

/** The latest {by, at} among a section's fields, from the field-attribution map. */
export function latestAttr(map: AttrMap, keys: string[]): { by: string | null; at: string | null } | null {
  let best: { by: string | null; at: string | null } | null = null;
  for (const k of keys) {
    const a = map[k];
    if (a && a.at && (!best || (best.at ?? '') < a.at)) best = a;
  }
  return best;
}

/**
 * The latest {by, at} across a list of rows carrying updated_at/updated_by — the
 * section attribution for a contacts panel (master-doc A5), where the "section"
 * is a list of contact rows rather than one record's field map.
 */
export function latestRowAttr(
  rows: { updated_at?: string | null; updated_by?: string | null }[],
): { by: string | null; at: string | null } | null {
  let best: { by: string | null; at: string | null } | null = null;
  for (const r of rows) {
    if (r.updated_at && (!best || (best.at ?? '') < r.updated_at)) {
      best = { by: r.updated_by ?? null, at: r.updated_at };
    }
  }
  return best;
}

/** Humanize a registry token (e.g. key_account → "Key account") for a dropdown label. */
export function humanizeToken(t: string): string {
  const s = t.replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Build InlineEditCard select options from an org-settings registry payload: a leading
 * "— Select —", then the firm's values sorted by label like every other dropdown. A
 * registry whose stored order is a ladder (value statuses) passes `preserveOrder`, and the
 * field that renders it must pass `preserveOrder` to the select as well.
 */
export function registryOptions(
  data: string[] | { values: string[] } | undefined,
  { preserveOrder = false }: { preserveOrder?: boolean } = {},
): { value: string; label: string }[] {
  const vals = Array.isArray(data) ? data : (data?.values ?? []);
  const options = vals.map((v) => ({ value: v, label: humanizeToken(v) }));
  return [{ value: '', label: '— Select —' }, ...(preserveOrder ? options : sortOptionsByLabel(options))];
}
